// organizerConfig.ts
import { Country, State } from "country-state-city";

//import images
import retirementImg from "../../assets/retirement-img.png";
import cryptoImg from "../../assets/crypto-img.png";
import interestImg from "../../assets/interest-img.png";
import collegeImg from "../../assets/college-img.png";
import studentLoanImg from "../../assets/student-loan-img.png";
import marketPlaceImg from "../../assets/marketplace-insurance-img.png";
import homeImg from "../../assets/home-img.png";
import movedImg from "../../assets/moved-img.png";
import charityImg from "../../assets/charity-img.png";
import medicalExpensesImg from "../../assets/medical-img.png";
import noneApplyImg from "../../assets/none-apply-img.png";
import StatusPage from "../../pages/TaxOrganizer/Components/Custom/StatusPage/StatusPage";
import DisclosureAgreement from "../../pages/TaxOrganizer/Components/Custom/DisclosureAgreement/DisclosureAgreement";
import ReviewPage from "../../pages/TaxOrganizer/Components/Custom/ReviewPage/ReviewPage";
import AdditionalComments from "../../pages/TaxOrganizer/Components/Custom/AdditionalComments/AdditionalComments";
import UploadDocNav from "../../pages/TaxOrganizer/Components/Custom/UploadDocArea/UploadDocNav";
import DocsUpload from "../../pages/TaxOrganizer/Components/Custom/UploadDocArea/DocsUpload";
import AdditionalQuestions from "../../pages/TaxOrganizer/Components/Dynamic/AdditionalQuestions/AdditionalQuestions";
import { OrganizerPages } from "@constants/TaxOrganizer.ts";
// need to add group groups so I can add titles and separate them on the page
const countryNames = Country.getAllCountries().map((country) => country.name);

const countryCode = "US";
const stateNames = State.getStatesOfCountry(countryCode)
  .map((state) => state.isoCode)
  .filter((state) => !state.includes("-"));
const updatedStateNames = [...stateNames, "Other State or Province"];

export const tiles = [
  {
    id: 1,
    name: "retirement",
    description: "I withdrew money from a retirement account",
    image: retirementImg,
  },
  {
    id: 2,
    name: "crypto",
    description: "I made or lost money from stocks or crypto",
    image: cryptoImg,
  },
  {
    id: 3,
    name: "interest",
    description: "I made interest income",
    image: interestImg,
  },
  {
    id: 4,
    name: "education",
    description: "I attended a college or university",
    image: collegeImg,
  },
  {
    id: 5,
    name: "studentLoan",
    description: "I paid student loan interest",
    image: studentLoanImg,
  },
  {
    id: 6,
    name: "insurance",
    description: "I received insurance from the marketplace",
    image: marketPlaceImg,
  },
  {
    id: 7,
    name: "home",
    description: "I bought or owned a home",
    image: homeImg,
  },
  {
    id: 8,
    name: "moved",
    description: "I moved last year or had a primary home in multiple states",
    image: movedImg,
  },
  {
    id: 9,
    name: "charity",
    description: "I made contributions to charity",
    image: charityImg,
  },
  {
    id: 10,
    name: "medical",
    description: "I paid for medical expenses",
    image: medicalExpensesImg,
  },
  {
    id: 11,
    name: "none",
    description: "None of these apply to me",
    image: noneApplyImg,
  },
];

const businessExpenseCategories = [
  { value: "", label: "-- Select None --" },
  { value: "Advertising", label: "Advertising" },
  { value: "Hotels", label: "Hotels" },
  { value: "Car and Truck Expenses", label: "Car and Truck Expenses" },
  { value: "Rental Cars", label: "Rental Cars" },
  { value: "Meals", label: "Meals" },
  { value: "Contract Labor", label: "Contract Labor" },
  { value: "Uniforms", label: "Uniforms" },
  {
    value: "Insurance (other than health)",
    label: "Insurance (other than health)",
  },
  { value: "Cell Phone Bill", label: "Cell Phone Bill" },
  {
    value: "Legal and Professional Services",
    label: "Legal and Professional Services",
  },
  { value: "Power Bill Year Total", label: "Power Bill Year Total" },
  { value: "Offices Expense", label: "Offices Expense" },
  { value: "Internet Bill Year Total", label: "Internet Bill Year Total" },
  { value: "Rent or Lease of Equipment", label: "Rent or Lease of Equipment" },
  { value: "Mileage", label: "Mileage" },
  { value: "Repairs or Maintenance", label: "Repairs or Maintenance" },
  { value: "Gas", label: "Gas" },
  { value: "Supplies", label: "Supplies" },
  { value: "Home Rent year total", label: "Home Rent year total" },
  { value: "Taxes", label: "Taxes" },
  { value: "Flight Tickets", label: "Flight Tickets" },
  { value: "Commissions and Fees", label: "Commissions and Fees" },
  { value: "Other", label: "Other" },
];

const generateFieldName = (prefix: string, baseName: string): string => {
  if (!prefix) return baseName;
  return `${prefix}${baseName.charAt(0).toUpperCase()}${baseName.slice(1)}`;
};

const generateAddressFields = (prefix: string) => [
  {
    type: "textInput",
    name: generateFieldName(prefix, "address"),
    label: "Address",
    required: true,
  },
  {
    type: "textInput",
    name: generateFieldName(prefix, "addressLine2"),
    label: "Address Line 2",
  },
  {
    type: "group",
    name: generateFieldName(prefix, "cityStateZipGroup"),
    layout: "two-column",
    fields: [
      {
        type: "textInput",
        name: generateFieldName(prefix, "city"),
        label: "City / Town",
        required: true,
      },
      {
        type: "select",
        options: updatedStateNames,
        name: generateFieldName(prefix, "state"),
        label: "State / Province",
        required: true,
      },
    ],
  },
  {
    type: "group",
    name: generateFieldName(prefix, "zipCountryGroup"),
    layout: "two-column",
    fields: [
      {
        type: "textInput",
        name: generateFieldName(prefix, "zip"),
        label: "Zip Code",
        required: true,
      },
      {
        type: "select",
        options: countryNames,
        name: generateFieldName(prefix, "country"),
        label: "Country",
        required: true,
      },
    ],
  },
];

const spouseIncomeInfoGroup = {
  type: "group",
  name: "spouseIncomeInfo",
  isTopLevel: true,
  fields: [
    {
      type: "choice",
      name: "spouseIncomeType",
      label: "How did your spouse make money this year?",
      options: [
        { value: "Worked as an employee", icon: "IconBuilding" },
        { value: "Received unemployment", icon: "IconBusinessplan" },
        { value: "Did freelance / gig work", icon: "IconDeviceLaptop" },
        { value: "No income", icon: "IconCashOff" },
        { value: "Other", icon: "IconSwipe" },
      ],
      multiselect: true,
      description: "Select all that apply to your spouse",
      optionFields: {
        "Worked as an employee": [
          {
            type: "group",
            name: "spouseNumJobsGroup",
            isTopLevel: true,
            fields: [
              {
                type: "numberInput",
                name: "spouseNumJobs",
                allowDecimal: false,
                label: "How many jobs did your spouse work for this year?",
                placeholder: "1",
                description:
                  "Please provide the total number of jobs your spouse worked for this year",
                min: 1,
              },
            ],
          },
        ],
        "Did freelance / gig work": [
          {
            type: "group",
            name: "spouseNumContractingJobsGroup",
            isTopLevel: true,
            fields: [
              {
                type: "numberInput",
                name: "spouseNumContractingJobs",
                allowDecimal: false,
                label: "How many 1099-NECs did your spouse receive this year?",
                placeholder: "1",
                description:
                  "Please provide the total number of NECs your spouse received for this year",
                min: 1,
              },
            ],
          },
        ],
        Other: [
          {
            type: "textInput",
            name: "spouseOtherIncome",
            label: "Other Income",
            description:
              "Please provide a detailed explanation of your spouse's income situation",
          },
        ],
      },
      exclusiveLastOption: false,
    },
  ],
};

export const organizerConfig: any = {
  "Getting Started": {
    title: "Getting Started",
    pages: {
      welcome: {
        title: "Welcome",
        type: "custom",
        component: StatusPage,
        props: {
          isConfirmation: false,
        },
      },
      // method1040: {
      //   type: "dynamic",
      //   title: "Did you file taxes last year?",
      //   layout: "row",
      //   fields: [
      //     {
      //       type: "dynamicGroup",
      //       name: "method1040",
      //       groupLabel: "Refund Instructions",
      //       canAddGroup: false,
      //       groups: [
      //         {
      //           fields: [
      //             {
      //               type: "group",
      //               name: "method1040",
      //               layout: "column",
      //               fields: [
      //                 {
      //                   type: "choice",
      //                   name: "method",
      //                   multiselect: false,
      //                   options: [
      //                     {
      //                       value: "Yes",
      //                       icon: "IconBuildingBank",
      //                     },
      //                     {
      //                       value: "No",
      //                       icon: "IconMailbox",
      //                     },
      //                   ],

      //                   optionFields: {
      //                     Yes: [
      //                       {
      //                         type: "group",
      //                         name: "1040Form",
      //                         label: "Upload 2023 Form 1040",
      //                         isTopLevel: true,
      //                         layout: "column",
      //                         fields: [
      //                           {
      //                             type: "component",
      //                             component: DocsUpload,
      //                             props: {
      //                               uploadTitleText: "1040",
      //                               showUploadTitle: false,
      //                               isMapped: false,
      //                             },
      //                           },
      //                         ],
      //                       },
      //                     ],
      //                     No: [],
      //                   },
      //                 },
      //               ],
      //             },
      //           ],
      //         },
      //       ],
      //     },
      //   ],
      // },
      personalInfo: {
        title: "Personal Information",
        type: "dynamic",
        layout: "row",
        fields: [
          {
            type: "dynamicGroup",
            name: "personalInfo",
            groupLabel: "Personal Info",
            canAddGroup: false,
            groups: [
              {
                fields: [
                  {
                    type: "group",
                    name: "filingStatusChoice",
                    isTopLevel: true,
                    fields: [
                      {
                        type: "choice",
                        name: "filingStatus",
                        label: "How will you be filing?",
                        options: [
                          { value: "Single", icon: "IconUser" },
                          {
                            value: "Married Filing Jointly",
                            icon: "IconCirclesRelation",
                          },
                          {
                            value: "Married Filing Separately",
                            icon: "IconUsersMinus",
                          },
                          {
                            value: "Head of Household",
                            icon: "IconBabyCarriage",
                          },
                          {
                            value: "Qualifying Surviving Spouse",
                            icon: "IconUserCircle",
                          },
                        ],
                        optionFields: {
                          "Married Filing Jointly": [spouseIncomeInfoGroup],
                          "Qualifying Surviving Spouse": [
                            spouseIncomeInfoGroup,
                          ],
                        },
                        modalField: "What's my filing status?",
                      },
                    ],
                  },
                  {
                    type: "group",
                    name: "money",
                    isTopLevel: true,
                    fields: [
                      {
                        type: "choice",
                        name: "incomeType",
                        label: "How did you make money this year?",
                        options: [
                          {
                            value: "I worked as an employee",
                            icon: "IconBuilding",
                          },
                          {
                            value: "I have a business",
                            icon: "IconBuildingStore",
                          },
                          {
                            value: "I received unemployment",
                            icon: "IconBusinessplan",
                          },
                          {
                            value: "I did freelance / gig work",
                            icon: "IconDeviceLaptop",
                          },
                          {
                            value: "Other",
                            icon: "IconSwipe",
                          },
                        ],
                        multiselect: true,
                        description: "Select all that apply to you",
                        optionFields: {
                          "I worked as an employee": [
                            {
                              type: "group",
                              name: "numJobsGroup",
                              isTopLevel: true,
                              groupLabel: "Number of Jobs",
                              layout: "column",
                              fields: [
                                {
                                  type: "numberInput",
                                  name: "numJobs",
                                  allowDecimal: false,
                                  label:
                                    "How many employers did you work for this tax year?",
                                  placeholder: "1",
                                  description:
                                    "Please provide the total number of employers you worked for this year",
                                  min: 1,
                                },
                              ],
                            },
                          ],
                          "I did freelance / gig work": [
                            {
                              type: "group",
                              name: "numContractingJobsGroup",
                              isTopLevel: true,
                              groupLabel: "Number of Jobs",
                              layout: "column",
                              fields: [
                                {
                                  type: "numberInput",
                                  name: "numContractingJobs",
                                  allowDecimal: false,
                                  label:
                                    "How many 1099-NECs did you receive this tax year?",
                                  placeholder: "1",
                                  description:
                                    "Please provide the total number of 1099-NECs you received this year",
                                  min: 1,
                                },
                              ],
                            },
                          ],
                          "I have a business": [
                            {
                              type: "group",
                              name: "businessExpenseCategoryGroup",
                              isTopLevel: true,
                              fields: [
                                {
                                  type: "numberInput",
                                  name: "numBusinesses",
                                  allowDecimal: false,
                                  label:
                                    "How many businesses have you owned this year?",
                                  placeholder: "1",
                                  description:
                                    "Please provide the total number of businesses you or your spouse (if applicable) owned this year",
                                  min: 1,
                                  max: 20,
                                  defaultValue: 1,
                                },
                              ],
                            },
                          ],
                          Other: [
                            {
                              type: "textInput",
                              name: "otherIncome",
                              label: "Other Income",
                              description:
                                "Please provide a detailed explanation of your income situation",
                            },
                          ],
                        },
                        exclusiveLastOption: false,
                      },
                    ],
                  },
                  {
                    type: "group",
                    name: "numDependentsChoice",
                    isTopLevel: true,
                    fields: [
                      {
                        type: "numberInput",
                        name: "numDependents",
                        allowDecimal: false,
                        label: "How many dependents will you claim?",
                        placeholder: "0",
                        description:
                          "Please select if you had children or anyone living with you that you financially supported this year.",
                        min: 0,
                        max: 30,
                        defaultValue: 0,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      docInfo: {
        title: "Now, let's make sure you get all your money!",
        type: "dynamic",
        fields: [
          {
            type: "dynamicGroup",
            name: "docInfo",
            groupLabel: "Documents",
            canAddGroup: false,
            groups: [
              {
                fields: [
                  {
                    type: "tiles",
                    label: "Do any of the following apply to you?",
                    description: "Select all that apply",
                    name: "tiles",
                    multiselect: true,
                  },
                ],
              },
            ],
          },
        ],
      },
    },
  },
  Basics: {
    title: "Identity Verification",
    pages: {
      identityVerification: {
        title: "Sweet! Let’s get to know you better",
        type: "dynamic",
        layout: "column",
        fields: [
          {
            type: "dynamicGroup",
            name: "identityVerification",
            groupLabel: "Identity Verification",
            canAddGroup: false,
            groups: [
              {
                fields: [
                  {
                    type: "group",
                    name: "uploadGovId",
                    label: "Upload your government issued ID",
                    layout: "column",
                    isTopLevel: true,
                    fields: [
                      {
                        type: "component",
                        component: DocsUpload,
                        props: {
                          uploadTitleText: "id",
                          showUploadTitle: false,
                          isMapped: false,
                        },
                      },
                    ],
                  },
                  {
                    type: "group",
                    name: "yourIdentity",
                    label: "Your Identity",
                    isTopLevel: true, // have this to add subtitle and space below
                    layout: "column",
                    fields: [
                      {
                        type: "group",
                        name: "name",
                        label: "Full Name",
                        layout: "three-column",
                        fields: [
                          {
                            type: "textInput",
                            name: "firstName",
                            label: "First Name",
                            required: true,
                          },
                          {
                            type: "textInput",
                            name: "middleName",
                            label: "Middle Name",
                          },
                          {
                            type: "textInput",
                            name: "lastName",
                            label: "Last Name",
                            required: true,
                          },
                        ],
                      },
                      {
                        type: "group",
                        name: "birthInfo",
                        label: "Birth Information",
                        layout: "two-column",
                        fields: [
                          {
                            type: "dateInput",
                            name: "dateOfBirth",
                            label: "Date of Birth",
                            required: true,
                          },
                          {
                            type: "textInput",
                            name: "ipPin",
                            info: {
                              title: "IP PIN (Identity Protection PIN)",
                              description: [
                                {
                                  id: 1,
                                  text: "An IP PIN is a four-digit number assigned by the IRS to help prevent identity theft and fraudulent tax returns.",
                                },
                                {
                                  id: 2,
                                  text: "If you have an IP PIN, you'll need to enter it when you file your tax return online. It's a security measure to ensure that only you, the legitimate taxpayer, can file your return.",
                                },
                              ],
                            },
                            label: "IP PIN (Optional)",
                          },
                        ],
                      },
                      {
                        type: "group",
                        name: "contactInfo",
                        label: "Contact Information",
                        layout: "two-column",
                        fields: [
                          {
                            type: "passwordInput",
                            name: "SSN",
                            label: "Social Security Number",
                            required: true,
                          },
                          {
                            type: "passwordInput",
                            name: "confirmSSN",
                            label: "Confirm Social Security Number",
                            required: true,
                          },
                        ],
                      },
                      {
                        type: "group",
                        name: "identification",
                        label: "Identification",
                        layout: "two-column",
                        fields: [
                          {
                            type: "phoneNumberInput",
                            name: "yourPhone",
                            label: "Phone Number",
                            required: true,
                          },
                          {
                            type: "textInput",
                            name: "occupation",
                            label: "Occupation",
                            required: true,
                          },
                        ],
                      },
                    ],
                  },
                  {
                    type: "group",
                    name: "address",
                    label: "Your Address",
                    isTopLevel: true,
                    layout: "column",
                    fields: generateAddressFields(""),
                  },
                ],
              },
            ],
          },
        ],
      },
      formerAddress: {
        title: "Now, we need some quick details about you",
        type: "dynamic",
        layout: "column",
        fields: [
          {
            type: "dynamicGroup",
            name: "formerAddress",
            groupLabel: "Former Address",
            canAddGroup: false,
            groups: [
              {
                fields: [
                  {
                    type: "group",
                    name: "formerAddress",
                    label:
                      "Provide the state and county you lived in last year",
                    isTopLevel: true,
                    layout: "column",
                    fields: [
                      {
                        type: "group",
                        name: "stateCounty",
                        layout: "column",
                        isTopLevel: true,
                        fields: [
                          {
                            type: "dynamicInputPair",
                            selectLabel: "State / Province",
                            inputLabel: "City / Town",
                            selectData: stateNames,
                            storedObjectName: "address",
                            fieldName: "formerAddress",
                            isSelectInput: true,
                          },
                        ],
                      },
                      {
                        type: "group",
                        name: "moreComments",
                        layout: "column",
                        label: "Add any comments for your Tax Pro",
                        isTopLevel: true,
                        fields: [
                          {
                            type: "component",
                            component: AdditionalComments,
                            props: {
                              parentObjectName: "formerAddress",
                            },
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    },
  },
  "Household Details": {
    title: "Household Details",
    pages: {
      spouseInfo: {
        title: "Great! Let’s gather some information about your spouse.",
        type: "dynamic",
        layout: "column",
        fields: [
          {
            type: "dynamicGroup",
            name: "spouseInfo",
            groupLabel: "Spouse",
            canAddGroup: false,
            groups: [
              {
                fields: [
                  {
                    type: "group",
                    name: "uploadSpouseGovId",
                    label: "Upload your spouse's government-issued ID",
                    layout: "column",
                    isTopLevel: true,
                    fields: [
                      {
                        type: "component",
                        component: DocsUpload,
                        props: {
                          showUploadPrompt: false,
                          isMapped: false,
                          showUploadTitle: false,
                          isSpouse: true,
                          uploadTitleText: "spouse-id",
                        },
                      },
                    ],
                  },
                  {
                    type: "group",
                    name: "spouseIdentity",
                    label: "Your Spouse's Identity",
                    layout: "column",
                    isTopLevel: true,
                    fields: [
                      {
                        type: "group",
                        name: "spouseInfo",
                        layout: "three-column",
                        fields: [
                          {
                            type: "textInput",
                            name: "spouseFirstName",
                            label: "First Name",
                            required: true,
                          },
                          {
                            type: "textInput",
                            name: "spouseMiddleName",
                            label: "Middle Name",
                          },
                          {
                            type: "textInput",
                            name: "spouseLastName",
                            label: "Last Name",
                            required: true,
                          },
                        ],
                      },
                      {
                        type: "group",
                        name: "spouseBirthInfo",
                        label: "Birth Information",
                        layout: "two-column",
                        fields: [
                          {
                            type: "dateInput",
                            name: "spouseDateOfBirth",
                            label: "Date of Birth",
                            required: true,
                          },
                          {
                            type: "textInput",
                            name: "spouseIpPin",
                            label: "IP PIN (Optional)",
                          },
                        ],
                      },
                      {
                        type: "group",
                        name: "spouseSSN",
                        label: "Social Security Number",
                        layout: "two-column",
                        fields: [
                          {
                            type: "passwordInput",
                            name: "spouseSSN",
                            label: "Social Security Number",
                            required: true,
                          },
                          {
                            type: "passwordInput",
                            name: "spouseConfirmSSN",
                            label: "Confirm Social Security Number",
                            required: true,
                          },
                        ],
                      },
                      {
                        type: "phoneNumberInput",
                        name: "spousePhone",
                        label: "Phone Number",
                        required: true,
                      },
                      {
                        type: "textInput",
                        name: "spouseOccupation",
                        label: "Occupation",
                        required: true,
                      },
                      {
                        type: "textInput",
                        name: "spouseEmail",
                        label: "Spouse Email",
                        required: true,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    },
  },
  Dependents: {
    pages: {
      dependentsInfo: {
        title: "We need some information about your dependents",
        name: "dependentsInfo",
        type: "dynamic",
        fields: [
          {
            type: "dynamicGroup",
            name: "dependentsInfo",
            groupLabel: "Dependent",
            useGroupCount: true,
            canAddGroup: true,
            initialGroupCountLabel: "numDependents", // this is the key in the responses object that corresponds to the number of dependents
            groups: [
              {
                fields: [
                  {
                    type: "group",
                    name: "dependentsInfo",
                    layout: "column",
                    isTopLevel: true,
                    fields: [
                      {
                        type: "group",
                        name: "dependentsInfo",
                        layout: "three-column",
                        fields: [
                          {
                            type: "textInput",
                            name: "firstName",
                            label: "First Name",
                            required: true,
                          },
                          {
                            type: "textInput",
                            name: "middleName",
                            label: "Middle Name",
                          },
                          {
                            type: "textInput",
                            name: "lastName",
                            label: "Last Name",
                            required: true,
                          },
                        ],
                      },
                      {
                        type: "group",
                        name: "dependentsSensitiveInfo",
                        layout: "two-column",
                        fields: [
                          {
                            type: "passwordInput",
                            name: "SSN",
                            label: "Social Security Number",
                            required: true,
                          },
                          {
                            type: "passwordInput",
                            name: "confirmSSN",
                            label: "Confirm Social Security Number",
                            required: true,
                          },
                        ],
                      },
                      {
                        type: "group",
                        name: "dependentsBirthInfo",
                        layout: "two-column",
                        fields: [
                          {
                            type: "dateInput",
                            name: "dateOfBirth",
                            label: "Date of Birth",
                            required: true,
                          },
                          {
                            type: "select",
                            name: "relationship",
                            label: "Relationship",
                            options: [
                              "Son",
                              "Daughter",
                              "Sister",
                              "Brother",
                              "Parent",
                              "Aunt",
                              "Uncle",
                              "Niece",
                              "Nephew",
                              "Grandparent",
                              "Foster Child",
                              "Grandchild",
                              "Half-Brother",
                              "Half-Sister",
                              "Step-Brother",
                              "Step-Sister",
                              "Other",
                            ],
                            required: true,
                          },
                        ],
                      },
                      {
                        type: "group",
                        name: "dependentsDayCare",
                        layout: "column",
                        isTopLevel: true,
                        fields: [
                          {
                            type: "checkbox-choice",
                            name: "dependentsDaycareChoice",
                            label: "Is this person in daycare?",
                            isRowLayout: true,
                            options: ["Yes", "No"],
                          },
                          {
                            type: "select",
                            name: "dependentsSelect",
                            label:
                              "During this tax year, how many months did you live with this dependent?",
                            options: [
                              "12",
                              "11",
                              "10",
                              "9",
                              "8",
                              "7",
                              "6",
                              "5",
                              "4",
                              "3",
                              "2",
                              "1",
                              "0",
                            ],
                            isDependentPage: true,
                          },
                          {
                            type: "checkbox-choice",
                            name: "dependentsMedicalChoice",
                            label:
                              "Did you pay any medical expenses or educational expenses for this dependent?",
                            isRowLayout: true,
                            options: ["Yes", "No"],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      daycareInfo: {
        title: "Tell us about your children’s daycare",
        type: "dynamic",
        layout: "column",
        fields: [
          {
            type: "dynamicGroup",
            name: "daycareInfo",
            groupLabel: "Daycare",
            canAddGroup: false,
            groups: [
              {
                fields: [
                  {
                    type: "group",
                    name: "daycareInfo",
                    layout: "column",
                    fields: [
                      {
                        type: "choice",
                        name: "dayCareSelect",
                        options: [
                          "My children attend the same daycare",
                          "My children attend different daycares",
                        ],
                        optionFields: {
                          "My children attend the same daycare": [
                            {
                              type: "group",
                              name: "daycareDetails",
                              label: "Daycare Information",
                              isTopLevel: true,
                              layout: "column",
                              fields: [
                                {
                                  type: "textInput",
                                  name: "daycareName",
                                  label: "Name of Daycare / Childcare Provider",
                                  required: true,
                                },
                                {
                                  type: "textInput",
                                  name: "dayCareEIN",
                                  label:
                                    "EIN or SSN for Daycare / Childcare Provider",
                                  required: true,
                                },
                                {
                                  type: "revenueInput",
                                  name: "daycareTuition",
                                  label:
                                    "How much did you pay in tuition for the entire year last year?",
                                  required: true,
                                },
                              ],
                            },
                          ],
                          "My children attend different daycares": [
                            {
                              type: "differentDaycares",
                              name: "daycareInfo",
                              labelOne: "Name of Daycare / Childcare Provider",
                              labelTwo:
                                "EIN or SSN for Daycare / Childcare Provider",
                              labelThree:
                                "How much did you pay in tuition for the entire year last year?",
                            },
                          ],
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    },
  },
  "Business Details": {
    pages: {
      businessInfo: {
        title: "We need a few details about your business",
        name: "businessInfo",
        type: "dynamic",
        layout: "column",
        fields: [
          {
            type: "dynamicGroup",
            name: "businessInfo",
            groupLabel: "Business",
            useGroupCount: true,
            canAddGroup: true,
            initialGroupCountLabel: "numBusinesses",
            groups: [
              {
                fields: [
                  {
                    type: "group",
                    name: "businessInfoGroup",
                    label: "Business Basics",
                    isTopLevel: true,
                    layout: "column",
                    fields: [
                      {
                        type: "textInput",
                        name: "businessName",
                        label: "Business Name",
                        required: true,
                      },
                      {
                        type: "textInput",
                        name: "employerIdentification",
                        label: "Employer Identification Number (EIN)",
                      },
                      {
                        type: "textInput",
                        name: "industry",
                        label: "Industry",
                        required: true,
                      },
                    ],
                  },
                  {
                    type: "group",
                    name: "businessAdress",
                    label: "Business Address",
                    layout: "column",
                    isTopLevel: true,
                    fields: generateAddressFields("business"),
                  },
                  {
                    type: "group",
                    name: "businessRevenue",
                    label: "Business Revenue",
                    isTopLevel: true,
                    layout: "column",
                    description:
                      "Enter how much money the company made in total last year.",
                    fields: [
                      {
                        type: "revenueInput",
                        name: "revenue",
                        required: true,
                      },
                    ],
                  },
                  {
                    type: "group",
                    name: "businessExpenses",
                    label: "Business Expenses",
                    isTopLevel: true,
                    layout: "column",
                    description:
                      "Please enter the total amount of business expenses for this year. You will be asked for an itemized explanation of each expense when you upload your documents later.",
                    fields: [
                      {
                        type: "dynamicInputPair",
                        selectLabel: "Category",
                        inputLabel: "Amount",
                        selectData: businessExpenseCategories,
                        isInputMonetary: true,
                        storedObjectName: "expenses",
                        isSelectInput: true,
                        clearable: true,
                      },
                    ],
                  },
                  {
                    type: "group",
                    name: "uploadDocuments",
                    label:
                      "Upload supplementary documents for your business expenses",
                    isTopLevel: true,
                    layout: "column",
                    description:
                      "Please upload any relevant documents as evidence of your business expenses.",
                    fields: [
                      {
                        type: "component",
                        component: DocsUpload,
                        props: {
                          uploadTitleText: "business",
                          bulkUploadMode: true,
                          maxBulkFiles: 50,
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    },
  },
  "Additional Questions": {
    title: "Additional Questions",
    pages: {
      additionalQuestions: {
        title: "",
        type: "dynamic",
        fields: [
          {
            type: "dynamicGroup",
            name: "questions",
            groupLabel: "Questions",
            canAddGroup: false,
            groups: [
              {
                fields: [
                  {
                    type: "component",
                    component: AdditionalQuestions,
                  },
                ],
              },
            ],
          },
        ],
      },
    },
  },
  Review: {
    title: "Review your information",
    pages: {
      reviewAll: {
        title: "Review your information",
        type: "dynamic",
        fields: [
          {
            type: "dynamicGroup",
            name: OrganizerPages.REVIEW,
            groupLabel: "Review",
            canAddGroup: false,
            groups: [
              {
                fields: [
                  {
                    type: "component",
                    component: ReviewPage,
                  },
                ],
              },
            ],
          },
        ],
      },
      comments: {
        title: "Do you have any comments for your Tax Pro?",
        type: "dynamic",
        fields: [
          {
            type: "dynamicGroup",
            name: "comments",
            groupLabel: "Additional Comments",
            canAddGroup: false,
            groups: [
              {
                fields: [
                  {
                    type: "component",
                    component: AdditionalComments,
                    props: {
                      parentObjectName: "additionalComments",
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
    },
  },
  "Credits and Deductions": {
    title: "Credits and Deductions",
    pages: {
      credits: {
        type: "dynamic",
        title: "Tell us about last year's charitable giving",
        info: {
          title:
            "The IRS lets you deduct charitable donations to qualified organizations, including:",
          list: [
            'Government entities:  Donations made "exclusively for public purposes" to states, US possessions, or the federal government.',
            "Charitable organizations: These can be community groups, trusts, or foundations that focus on charity, religion, education, science, literature, or preventing animal/child cruelty.",
            "Religious organizations: Churches, synagogues, and other religious groups qualify.",
            "Veterans organizations: Donations to war veterans' organizations and their associated groups are deductible.",
            "Volunteer fire companies & civil defense: Non-profit fire companies and civil defense organizations qualify for your donations.",
            "Fraternal societies: Donations to domestic fraternal societies used solely for charitable purposes are deductible.",
            "Cemetery companies: Donations to non-profit cemetery companies specifically dedicated to the cemetery's overall care qualify.",
          ],
        },
        name: "credits",
        fields: [
          {
            type: "dynamicGroup",
            name: "credits",
            groupLabel: "Charitable Giving",
            canAddGroup: false,
            groups: [
              {
                fields: [
                  {
                    type: "group",
                    name: "charityGroup",
                    layout: "column",
                    label:
                      "Provide the charity name and amount you have donated",
                    isTopLevel: true,
                    fields: [
                      {
                        type: "dynamicInputPair",
                        selectLabel: "Charity Name",
                        inputLabel: "Amount",
                        isInputMonetary: true,
                        storedObjectName: "charity",
                        isSelectInput: false,
                      },
                    ],
                  },
                  {
                    type: "group",
                    name: "uploadDocuments",
                    label:
                      "Upload supplementary documents for your charitable giving",
                    isTopLevel: true,
                    layout: "column",
                    fields: [
                      {
                        type: "component",
                        component: DocsUpload,
                        props: {
                          uploadTitleText: "credits",
                          showUploadTitle: false,
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      medicalExpenses: {
        type: "dynamic",
        title: "Medical Expenses",
        name: "medicalExpenses",
        fields: [
          {
            type: "dynamicGroup",
            name: "medicalExpenses",
            groupLabel: "Medical expenses",
            canAddGroup: false,
            groups: [
              {
                fields: [
                  {
                    type: "group",
                    name: "medicalGroup",
                    layout: "column",
                    label:
                      "Provide the healthcare provider and the amount you have paid",
                    isTopLevel: true,
                    fields: [
                      {
                        type: "dynamicInputPair",
                        selectLabel: "Provider",
                        inputLabel: "Amount",
                        isInputMonetary: true,
                        storedObjectName: "medical",
                        isSelectInput: false,
                      },
                    ],
                  },
                  {
                    type: "group",
                    name: "uploadDocuments",
                    label:
                      "Upload supplementary documents for your medical expenses",
                    isTopLevel: true,
                    layout: "column",
                    fields: [
                      {
                        type: "component",
                        component: DocsUpload,
                        props: {
                          uploadTitleText: "medical",
                          showUploadTitle: false,
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    },
  },
  Documents: {
    title: "Documents",
    pages: {
      uploadedDocs: {
        title: "Great! Now, please upload the required forms below.",
        type: "dynamic",
        fields: [
          {
            type: "dynamicGroup",
            canAddGroup: false,
            name: "uploadDocs",
            groups: [
              {
                fields: [
                  {
                    type: "group",
                    name: "docsUploadArea",
                    layout: "two-column-big-gap",
                    fields: [
                      {
                        type: "group",
                        name: "uploadDocuments",
                        isTopLevel: true,

                        fields: [
                          {
                            type: "component",
                            component: DocsUpload,
                            props: {
                              showUploadPrompt: true,
                              isMapped: true,
                            },
                          },
                        ],
                      },
                      {
                        type: "group",
                        name: "uploadDocuments",
                        isTopLevel: true,
                        fields: [
                          {
                            type: "component",
                            component: UploadDocNav,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      uploadDocsOther: {
        title: "Upload any other documents you've received this year.",
        type: "dynamic",
        fields: [
          {
            type: "dynamicGroup",
            canAddGroup: false,
            name: "uploadDocs",
            groups: [
              {
                fields: [
                  {
                    type: "group",
                    name: "docsUploadArea",
                    layout: "two-column",

                    fields: [
                      {
                        type: "group",
                        name: "uploadDocuments",
                        label:
                          "This may include Retirement Income, Savings & Investments, Rental Income, Self-Employment forms, etc.",
                        isTopLevel: true,

                        fields: [
                          {
                            type: "component",
                            component: DocsUpload,
                            props: {
                              showUploadPrompt: false,
                              isMapped: false,
                              showUploadTitle: false,
                              uploadTitleText: "other",
                              bulkUploadMode: true,
                              maxBulkFiles: 50,
                            },
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    },
  },
  "Refund Instructions": {
    title: "Refund Instructions",
    pages: {
      refundMethod: {
        type: "dynamic",
        title:
          "Now, the fun part! How would you like your refund returned to you?",
        name: "refundInstructions",
        description: {
          beforeBoldText: "Please note that",
          boldText: "receiving a refund depends on your tax situation,",
          afterBoldText: "and you may owe taxes instead.",
        },
        fields: [
          {
            type: "dynamicGroup",
            name: "refundMethod",
            groupLabel: "Refund Instructions",
            canAddGroup: false,
            groups: [
              {
                fields: [
                  {
                    type: "group",
                    name: "refundMethod",
                    layout: "column",
                    fields: [
                      {
                        type: "choice",
                        name: "method",
                        multiselect: false,
                        options: [
                          {
                            value: "Deposit my refund into my bank account",
                            icon: "IconBuildingBank",
                          },
                          {
                            value:
                              "Send my refund check as a check in the mail",
                            icon: "IconMailbox",
                          },
                        ],
                        optionFields: {
                          "Deposit my refund into my bank account": [
                            {
                              type: "group",
                              name: "bankAccount",
                              label: "Bank Account Information",
                              isTopLevel: true,
                              layout: "column",
                              fields: [
                                {
                                  type: "checkbox-choice",
                                  name: "accountType",
                                  label: "Account Type",
                                  options: [
                                    "Checking Account",
                                    "Savings Account",
                                  ],
                                },
                                {
                                  type: "textInput",
                                  name: "routingNumber",
                                  label: "Routing Number",
                                  required: true,
                                },
                                {
                                  type: "textInput",
                                  name: "accountNumber",
                                  label: "Account Number",
                                  required: true,
                                },
                                {
                                  type: "textInput",
                                  name: "confirmAccountNumber",
                                  label: "Confirm Account Number",
                                  required: true,
                                },
                              ],
                            },
                          ],
                          "Send my refund check as a check in the mail": [
                            {
                              type: "group",
                              name: "mailingAddress",
                              label: "Mailing Address",
                              isTopLevel: true,
                              layout: "column",
                              fields: generateAddressFields("mailing"),
                            },
                          ],
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    },
  },
  "Disclosure Agreement": {
    title: "Disclosure Agreement",
    pages: {
      agreement: {
        title: "Disclosure Agreement",
        name: "agreement",
        type: "custom",
        component: DisclosureAgreement,
        props: {
          parentObjectName: "disclosureAgreement",
        },
      },
      confirm: {
        title: "Welcome",
        type: "custom",
        component: StatusPage,
        props: {
          isConfirmation: true,
        },
      },
    },
  },

  // ... other steps ...
};
